import { Component, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { NewUiButton } from "@ankaadia/ankaadia-shared-frontend";
import { NewUiDashboardSharedCollectionData, SharingTypeEnum } from "@ankaadia/graphql";
import { EmptystateComponent } from "@ankaadia/ui";
import { derivedAsync } from "ngxtension/derived-async";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "primeng/tabs";
import { Skeleton } from "primeng/skeleton";
import { ToggleSwitch } from "primeng/toggleswitch";
import { Popover } from "primeng/popover";
import { catchError, map, of, switchMap } from "rxjs";
import { OrganizationSystemSettingsService } from "../../organizations/system-settings.service";
import { SettingsService } from "../../../shared/services/settings.service";
import { TranslateDirective } from "../../../shared/transloco/translate.directive";
import { NewUiDashboardCardHeaderComponent } from "../layout/new-ui-dashboard-card-header/new-ui-dashboard-card-header.component";
import { NewUiDashboardCardComponent } from "../layout/new-ui-dashboard-card/new-ui-dashboard-card.component";
import {
  DashboardListItem,
  NewUiDashboardListComponent,
} from "../layout/new-ui-dashboard-list/new-ui-dashboard-list.component";
import { NewUiDashboardService } from "../new-ui-dashboard.service";

@Component({
  selector: "new-ui-dashboard-sharing",
  templateUrl: "./new-ui-dashboard-sharing.component.html",
  imports: [
    TranslateDirective,
    NewUiDashboardCardComponent,
    NewUiDashboardCardHeaderComponent,
    NewUiDashboardListComponent,
    TabList,
    Tab,
    Tabs,
    TabPanels,
    TabPanel,
    EmptystateComponent,
    Skeleton,
    FormsModule,
    ToggleSwitch,
    Popover,
    NewUiButton,
  ],
})
export class NewUiDashboardSharingComponent {
  private readonly newUiDashboardService = inject(NewUiDashboardService);
  protected readonly settings = inject(SettingsService);
  private readonly systemSettings = inject(OrganizationSystemSettingsService);

  protected readonly hideEmptyLists = signal<boolean>(false);

  constructor() {
    this.systemSettings
      .get(this.settings.organizationId)
      .pipe(
        takeUntilDestroyed(),
        map((x) => x.hideEmptyLists ?? false)
      )
      .subscribe((value) => this.hideEmptyLists.set(value));
  }

  private readonly sharedCollections$ = toObservable(this.hideEmptyLists).pipe(
    switchMap((hideEmptyLists) =>
      this.newUiDashboardService.getSharedCollections(hideEmptyLists).pipe(
        map((data) => ({
          totalSharings: data.totalCount,
          readAndWrite: data.collections
            .filter(
              (x) =>
                x.sharingType === SharingTypeEnum.Collaboration || x.sharingType === SharingTypeEnum.ReadOnlyCollaboration
            )
            .map((x) => this.mapCollectionToListItem(x)),
          presentation: data.collections
            .filter((x) => x.sharingType === SharingTypeEnum.View)
            .map((x) => this.mapCollectionToListItem(x)),
        }))
      )
    )
  );

  protected readonly sharedCollections = derivedAsync(() => this.sharedCollections$.pipe(catchError(() => of(null))));

  private mapCollectionToListItem(collection: NewUiDashboardSharedCollectionData): DashboardListItem {
    return {
      rowIcon:
        collection.sharingType === SharingTypeEnum.Collaboration
          ? "icon-[mi--pencil-edit-create-3]"
          : collection.sharingType === SharingTypeEnum.ReadOnlyCollaboration
            ? "icon-[mi--glasses]"
            : "",
      rowIconStyleClass:
        collection.sharingType === SharingTypeEnum.Collaboration
          ? "text-warning-soft"
          : collection.sharingType === SharingTypeEnum.ReadOnlyCollaboration
            ? "text-success"
            : "",
      name: collection.collectionName,
      tagIcon: "icon-[mi--employee-badge]",
      tag: collection.candidateCount > 99 ? "99+" : collection.candidateCount?.toString(),
      groupIcon: "icon-[mi--high-rise-building]",
      group: collection.ownerOrganizationName,
      routeLink:
        collection.sharingType === SharingTypeEnum.View
          ? `/app/candidates/view/${collection.ownerOrganizationId}/${collection.collectionId}`
          : "/app/candidates/edit",
    };
  }
}
